import React, { useState, useEffect, useRef } from 'react';
import AlertAlgo from '../controls/AlertSnack';
import { withNamespaces } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import i18next from 'i18next';
import { log } from '../../actions/error';
import Button from 'react-bootstrap-button-loader';
import Link from '@material-ui/core/Link';
import * as storage from '../../utils/storage';
import { subscribe, unsubscribe } from '../../actions/notification';
import Switch from '@material-ui/core/Switch';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import * as StatusCode from '../../scripts/StatusCode';
import { openDatabase, storeData, fetchData } from '../../utils/indexedDB';
import { storesDB } from '../../data/storesDB';
import { encrypt } from '../../utils/encryption';

const useStyles = makeStyles(theme => ({
    layer1: {
        position: 'absolute',
        padding: '10px',
        transition: 'all 1000ms',
        marginTop: '-350px',
        left: '0',
        right: '0',
        marginLeft: 'auto',
        marginRight: 'auto',
        width: 'fit-content'
    },
    layer1After: {
        position: 'absolute',
        padding: '10px',
        transition: 'all 1000ms',
        marginTop: '0px',
        left: '0',
        right: '0',
        marginLeft: 'auto',
        marginRight: 'auto',
        width: 'fit-content'
    },
    layer2: {
        padding: '10px',
        backgroundColor: 'rgb(255 255 255 / 90%)',
        borderRadius: '20px',
        border: '1px solid #c1c1c1'
    },
    layer3: {
        padding: '10px'
    },
    btnNo: {
        backgroundColor: '#810000 !important',
        borderColor: '#810000 !important',
        marginRight: '10px',
        '&:hover:enabled': {
            backgroundColor: '#810000 !important',
            borderColor: '#810000 !important',
            boxShadow: 'unset !important'
        }
    },
    btn: {
        fontSize: '12px'
    },
    link: {
        display: 'table',
        width: '100%',
        fontSize: '12px',
        marginTop: '10px',
    }
}));

function Subscribe(prosp) {
    const classes = useStyles();
    const [cssAnimation, setCssAnimation] = useState(classes.layer1);

    const [language, setLanguage] = useState(storage.getLanguage());
    const [error, setError] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [showPopupNoPrivate, setShowPopupNoPrivate] = useState(false);

    const [db, setDb] = useState(null);

    const alertRef = useRef(null);

    useEffect(() => {
        init();
    }, [])

    useEffect(() => {
        if (showPopup) {
            setCssAnimation(classes.layer1After);
        } else {
            setCssAnimation(classes.layer1);
        }
    }, [showPopup])

    useEffect(() => {
        if (error) {
            showMessageError(i18next.t('error:error-subscription'));
        }
    }, [error])

    const openDB = () => {
        return new Promise((resolve, reject) => {

            const stores = [storesDB.settings];

            openDatabase(stores).then(async (database) => {
                setDb(database);
                resolve(database);
            }).catch(error => {
                showMessageError(i18next.t('error:generic-error'));
                reject(null);
            });
        })
    }

    const getPrivateKey = (database) => {
        return new Promise((resolve, reject) => {
            if (database) {
                fetchData(database, storesDB.settings.name, storesDB.settings.columns.NotificationPrivateKey).then(item => {
                    if (item != null) {
                        resolve(item.value);
                    } else {
                        resolve(null);
                    }
                });
            } else {
                reject(null);
            }
        })
    }

    const init = async () => {
        const isNotificationActivate = storage.getIsNotificationActivate();

        if (isNotificationActivate) {
            const db = await openDB();

            const privateKey = await getPrivateKey(db);

            manageDisplaySubscription(privateKey != null);
        }
    }

    const manageDisplaySubscription = async (hasPrivateKey) => {
        const showSubscribe = storage.getShowSubscribe() ?? true;
        if (showSubscribe) {
            const hasSubscription = await verifySubscription();

            if (!hasPrivateKey && hasSubscription) {
                setShowPopupNoPrivate(true);
                setShowPopup(true);
                return;
            }

            setTimeout(() => {
                setShowPopup(!hasSubscription);
            }, 2000)
        }
    }

    const verifySubscription = () => {
        return new Promise(resolve => {
            // Check if the device has already a subscription
            navigator.serviceWorker.ready.then((reg) => {
                reg.pushManager.getSubscription().then((subscription) => {
                    resolve(subscription != null);
                });
            });
        })
    }

    const showMessageSuccess = (message) => {
        alertRef.current.showMessageSuccess(message);
    }

    const showMessageError = (message) => {
        alertRef.current.showMessageError(message);
    }

    const createSubscription = (isCreate) => {
        if (showPopupNoPrivate) {
            // If already have a subscription but the private key is not registered
            // so we delete the current subscription before create the new one
            unsubscribeNotification();
        }

        if (!isCreate) {
            setShowPopup(false);
            return;
        }

        // ** To send the push notification we will need the private, public, endpoint, p256dh and auth keys **

        // 1 - The private / public keys are already generated by the server
        const publicKey = storage.getNotificationPublicKey();

        if (publicKey != null && publicKey.length > 0) {
            // 2 - We have to subscribe from the service worker
            navigator.serviceWorker.ready.then((sw) => {
                sw.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: publicKey
                }).then((push) => {
                    const data = JSON.parse(JSON.stringify(push));

                    setShowPopup(false);

                    subscribe(data.endpoint, data.keys.p256dh, data.keys.auth, language).then(response => {
                        if (response != null && response.status == StatusCode.STATUS_OK) {
                            savePrivateKey(encrypt(response.privateKey));
                            showMessageSuccess(i18next.t('pushNotification:successSubscribedMessage'));
                        } else {
                            unsubscribe();
                            showMessageError(i18next.t('error:generic-error'));
                        }
                    })
                }).catch((err) => {
                    log(err);
                    setError(true);
                });
            });
        } else {
            setError(true);
        }
    }

    const handleNotShow = (event) => {
        event.preventDefault();
        storage.saveShowSubscribe(false);
        setShowPopup(false);

        if (showPopupNoPrivate) {
            // If already have a subscription but the private key is not registered
            // so we delete the current subscription before create the new one
            unsubscribeNotification();
        }
    }

    const unsubscribeNotification = () => {
        navigator.serviceWorker.ready.then((reg) => {
            reg.pushManager.getSubscription().then((subscription) => {
                subscription
                    .unsubscribe()
                    .then((successful) => {
                        unsubscribe(subscription.endpoint).then(response => {
                            if (response == null || response.status != StatusCode.STATUS_OK) {
                                showMessageError(i18next.t('error:generic-error'));
                            }
                        })
                    })
                    .catch((e) => {
                        // Unsubscribing failed
                        showMessageError(i18next.t('error:generic-error'));
                    });
            });
        });
    }

    const handleChangeLanguage = (event) => {
        const language = event.target.checked ? "fr" : "en";

        setLanguage(language);
    }

    const savePrivateKey = (privateKey) => {
        if (db) {
            const setting = { code: storesDB.settings.columns.NotificationPrivateKey, value: privateKey };
            storeData(db, storesDB.settings.name, setting);
        }
    }

    return (
        <div className={cssAnimation}>
            <div className={classes.layer2}>
                <div className={classes.layer3}>
                    <span>{i18next.t(`pushNotification:${showPopupNoPrivate ? 'noPrivateKey' : 'description'}`)}</span>
                </div>
                <Typography component="div" style={{ margin: '0 0 10px 0' }}>
                    <Grid component="label" container alignItems="center" spacing={1} style={{ placeContent: 'center' }}>
                        <Grid item>EN</Grid>
                        <Grid item>
                            <Switch checked={language == "fr"} onChange={handleChangeLanguage} name="language" />
                        </Grid>
                        <Grid item>FR</Grid>
                    </Grid>
                </Typography>
                <Button className={`${classes.btnNo} ${classes.btn}`} onClick={() => { createSubscription(false) }} variant="success">
                    {i18next.t('pushNotification:noThanks')}
                </Button>
                <Button className={classes.btn} onClick={() => { createSubscription(true) }} variant="success">
                    {i18next.t('pushNotification:allow')}
                </Button>
                <Link href="#" className={classes.link} onClick={handleNotShow}>
                    {i18next.t('pushNotification:doNotShowMe')}
                </Link>
            </div>
            <AlertAlgo ref={alertRef} />
        </div>
    )
}

export default withNamespaces(['error', 'pushNotification'], { wait: true })(Subscribe);